// ════════════════════════════════════════════════════════════
// CalendarioTareas — vista de calendario para el módulo Tareas
// Guardar en: src/components/CalendarioTareas.tsx
// ════════════════════════════════════════════════════════════
import React from 'react';
import { Calendar, dateFnsLocalizer, View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { es } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Box } from '@mui/material';

const locales = { es };
const localizer = dateFnsLocalizer({
  format, parse, startOfWeek, getDay, locales,
});

// Textos en español
const messages = {
  today: 'Hoy',
  previous: 'Anterior',
  next: 'Siguiente',
  month: 'Mes',
  week: 'Semana',
  day: 'Día',
  agenda: 'Agenda',
  date: 'Fecha',
  time: 'Hora',
  event: 'Tarea',
  noEventsInRange: 'No hay tareas en este periodo.',
  showMore: (total: number) => `+ ${total} más`,
};

const PRIORIDAD_COLOR: any = {
  alta: '#ef4444',
  media: '#f59e0b',
  baja: '#22c55e',
};

interface Props {
  tareas: any[];
  onSelectTarea: (tarea: any) => void;
  onSelectDia: (fecha: string) => void;
}

export default function CalendarioTareas({ tareas, onSelectTarea, onSelectDia }: Props) {
  const [vista, setVista] = React.useState<View>('month');
  const [fecha, setFecha] = React.useState(new Date());

  // Convertir tareas a eventos del calendario (las que tienen fecha límite)
  const eventos = tareas
    .filter(t => t.fecha_limite)
    .map(t => {
      const d = new Date(t.fecha_limite + 'T00:00:00');
      return {
        id: t.id,
        title: t.titulo,
        start: d,
        end: d,
        allDay: true,
        recurso: t,
      };
    });

  return (
    <Box sx={{
      height: 650,
      // Estilos para integrar con el tema (colores, tipografía)
      '& .rbc-toolbar button': { color: 'text.primary' },
      '& .rbc-toolbar button.rbc-active': { bgcolor: '#4f46e5', color: '#fff' },
      '& .rbc-today': { bgcolor: 'rgba(245, 194, 0, 0.12)' },
      '& .rbc-off-range-bg': { bgcolor: 'action.hover' },
      '& .rbc-header': { py: 1, fontWeight: 600 },
    }}>
      <Calendar
        localizer={localizer}
        events={eventos}
        messages={messages}
        culture="es"
        view={vista}
        onView={(v: View) => setVista(v)}
        date={fecha}
        onNavigate={(d: Date) => setFecha(d)}
        views={['month', 'week', 'day']}
        startAccessor="start"
        endAccessor="end"
        style={{ height: '100%' }}
        onSelectEvent={(ev: any) => onSelectTarea(ev.recurso)}
        onSelectSlot={(slot: any) => {
          // Pulsar un día vacío → crear tarea en esa fecha
          const f = format(slot.start, 'yyyy-MM-dd');
          onSelectDia(f);
        }}
        selectable
        eventPropGetter={(ev: any) => {
          const prio = ev.recurso?.prioridad || 'media';
          const completada = ev.recurso?.estado === 'completada';
          return {
            style: {
              backgroundColor: completada ? '#9ca3af' : PRIORIDAD_COLOR[prio],
              borderRadius: 6,
              opacity: completada ? 0.6 : 1,
              textDecoration: completada ? 'line-through' : 'none',
              border: 'none',
              fontSize: 12,
            },
          };
        }}
      />
    </Box>
  );
}